/* Shared viewport and overlay state. Never measure Shopify's full-screen iframe.
   state() follows mutations and resizes. Scrolling runs no script here: the contact overlap is reported by an
   IntersectionObserver. Attributes and properties are written only when their value changes. */
(() => {
  'use strict';
  const html = document.documentElement;
  const overlays = 'cart-drawer.active, quick-add-modal[open], product-modal[open], .menu-opening, #Details-HeaderCategories[open], details[open] .search-modal, .xe-summer-popup.is-open:not(.is-sheet)';
  const exploring = 'xe-product-media [data-xe-media-tabs]:not([hidden]), xe-product-viewer [data-xe-3d-controls]:not([hidden]), .product-form__buttons, .xe-studio-button, .xe-button, .xe-summer-feature__cta, .product-form__input, .xe-cta__buttons';
  let bar, resizeObserver, frame = 0, overlayOpen = false, zone = null, rootSize = null;
  function setAttr(name, value) { if (html.getAttribute(name) !== value) { if (value === null) html.removeAttribute(name); else html.setAttribute(name, value); } }
  function setVar(name, value) { if (html.style.getPropertyValue(name) !== value) { if (value) html.style.setProperty(name, value); else html.style.removeProperty(name); } }
  function state() {
    frame = 0;
    const wrapper = document.getElementById('PBarNextFrameWrapper');
    if (bar !== wrapper) {
      resizeObserver?.disconnect();
      bar = wrapper;
      if (bar && window.ResizeObserver) {
        resizeObserver = new ResizeObserver(scheduleState);
        resizeObserver.observe(bar);
      }
    }
    setVar('--xe-preview-inset', bar ? `${Math.max(0, Math.ceil(bar.getBoundingClientRect().height))}px` : '');
    setAttr('data-xe-preview-measured', bar ? '' : null);
    const next = Boolean(document.querySelector(overlays));
    if (next !== overlayOpen) {
      overlayOpen = next;
      html.dataset.xeOverlay = String(next);
      document.dispatchEvent(new CustomEvent('xe:overlay', { detail: { open: next } }));
    }
    const viewport = window.visualViewport;
    setVar('--xe-viewport-height', `${Math.round(viewport?.height || window.innerHeight)}px`);
    setAttr('data-xe-keyboard', String(Boolean(viewport && viewport.height < window.innerHeight * .75)));
    watch();
  }
  // Keep shopping actions and product exploration clear of the floating contact link. The link is fixed, so its corner
  // (plus 8px) is measured here and becomes the root of an IntersectionObserver that reports the controls passing under it.
  function watch() {
    zone?.observer.disconnect();
    zone = null;
    const contact = document.querySelector('.xe-whatsapp-contact');
    const box = contact && contact.getClientRects().length ? contact.getBoundingClientRect() : null;
    const controls = document.querySelectorAll(exploring);
    if (!box || !box.width || !box.height || !controls.length || !window.IntersectionObserver) { setAttr('data-xe-3d-contact-overlap', 'false'); return; }
    const width = rootSize ? rootSize.width : html.clientWidth, height = rootSize ? rootSize.height : html.clientHeight;
    const right = Math.round(box.right + 8 - width), bottom = Math.round(box.bottom + 8 - height);
    const options = { rootMargin: `${Math.round(8 - box.top)}px ${right}px ${bottom}px ${Math.round(8 - box.left)}px` };
    let observer;
    try { observer = new IntersectionObserver(seen, Object.assign({ root: document }, options)); } catch (error) { observer = new IntersectionObserver(seen, options); }
    zone = { observer, width, height, right, bottom, under: new Set() };
    controls.forEach(element => observer.observe(element));
  }
  function seen(entries, observer) {
    if (!zone || observer !== zone.observer) return;
    const root = entries[0].rootBounds;
    // The first answer shows the observer's real viewport, which phone toolbars can make differ from the estimate.
    if (root && !rootSize) {
      rootSize = { width: root.right - zone.right, height: root.bottom - zone.bottom };
      if (Math.abs(rootSize.width - zone.width) > 1 || Math.abs(rootSize.height - zone.height) > 1) { watch(); return; }
    }
    entries.forEach(({ target, isIntersecting, boundingClientRect: box }) => {
      if (isIntersecting && box.width > 0 && box.height > 0 && !target.closest('[hidden]')) zone.under.add(target);
      else zone.under.delete(target);
    });
    setAttr('data-xe-3d-contact-overlap', String(zone.under.size > 0));
  }
  function scheduleState() { if (!frame) frame = requestAnimationFrame(state); }
  function resized() { rootSize = null; scheduleState(); }
  function init() {
    state();
    new MutationObserver(records => {
      if (records.some(record => record.type === 'childList' || record.target.matches('cart-drawer, quick-add-modal, product-modal, details, header-drawer, .xe-summer-popup, #PBarNextFrameWrapper, [data-xe-3d-panel], [data-xe-media-tabs], [data-xe-3d-controls]'))) scheduleState();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open', 'hidden'] });
    window.addEventListener('resize', resized, { passive: true });
    window.visualViewport?.addEventListener('resize', resized, { passive: true });
    // The contact link glides when the preview bar inset or its scroll hiding changes: measure its corner again where it stops.
    document.addEventListener('transitionend', event => { if ((event.propertyName === 'bottom' || event.propertyName === 'transform') && event.target.classList?.contains('xe-whatsapp-contact')) scheduleState(); });
    document.addEventListener('shopify:section:load', scheduleState);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

/* Product proof items rise into place once as they scroll into view (moved here from xe-studio.js, which loads on the home page only). */
(() => {
  'use strict';
  if (!window.IntersectionObserver) return;
  const registry = new Map(), reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const motion = () => document.body.classList.contains('xe-motion') && !reduce.matches;
  function setup(scope = document) {
    scope.querySelectorAll('.xe-product-proof__item').forEach((element, index) => {
      if (registry.has(element)) return;
      let animation;
      const observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        if (motion() && element.animate) animation = element.animate([{ opacity: .6, transform: 'translateY(16px)' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: index % 4 * 55, easing: 'cubic-bezier(.22,1,.36,1)' });
      }, { threshold: .12 });
      const stop = () => { if (reduce.matches) animation?.finish(); };
      reduce.addEventListener('change', stop);
      observer.observe(element);
      registry.set(element, () => { observer.disconnect(); animation?.cancel(); reduce.removeEventListener('change', stop); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  else setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => { for (const [element, destroy] of registry) if (event.target.contains(element)) { destroy(); registry.delete(element); } });
})();

/* Normalize storefront punctuation, including content refreshed by Shopify. */
(() => {
  'use strict';
  const excluded = 'script, style, noscript, textarea, input, [contenteditable]';
  const excludedAttributes = 'script, style, noscript, [contenteditable]';
  const attributes = ['aria-label', 'title', 'alt', 'placeholder'];
  const dash = /\s*\u2014\s*/g;
  const range = /(\d)\s*\u2014\s*(\d)/g;
  const leading = /^\s*\u2014\s*/;
  function normalize(text) { return text.replace(range, '$1 to $2').replace(dash, ': '); }
  /* In running text a comma reads naturally; a dash that follows a label element becomes a colon. */
  function normalizeText(node) {
    let text = node.data.replace(range, '$1 to $2');
    const previous = node.previousSibling;
    if (previous && previous.nodeType === Node.ELEMENT_NODE && leading.test(text)) text = text.replace(leading, ': ');
    return text.replace(dash, ', ');
  }
  function cleanAttributes(element) {
    if (element.closest(excludedAttributes)) return;
    for (const name of attributes) {
      const value = element.getAttribute(name);
      if (value?.includes('\u2014')) element.setAttribute(name, normalize(value));
    }
  }
  function clean(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      if (root.data.includes('\u2014') && !root.parentElement?.closest(excluded)) root.data = normalizeText(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    cleanAttributes(root);
    if (root.closest(excluded)) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.data.includes('\u2014') && !node.parentElement?.closest(excluded)) node.data = normalizeText(node);
      } else cleanAttributes(node);
    }
  }
  function init() {
    clean(document.body);
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'childList') record.addedNodes.forEach(clean);
        else if (record.type === 'characterData') clean(record.target);
        else cleanAttributes(record.target);
      }
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: attributes });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
